import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Loader2, ChevronRight, User, LocateFixed } from 'lucide-react';
import {
  fetchAutocomplete,
  type AutocompleteItem,
} from '../../../services/autocompleteService';
import { findMatchingProfessionsSimple, findMatchingProfessionsStrict } from '../../../utils/DefaultProfessions';
import {
  isBookingAskAvailable,
} from '../../../utils/prosDiscoverySearch';
import { submitDiscoveryOrAsk } from '../../../utils/submitDiscoveryOrAsk';
import { useGeoCountry } from '../../../hooks/useGeoCountry';
import { placesSearchCountryRestriction } from '../../../config/geoLocale';
import { FeatureFlag, useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { useGoogleMaps, useGoogleMapsConsumer } from '../../../contexts/GoogleMapsLoaderContext';
import {
  GeolocationRequestError,
  getCurrentCoordinates,
  reverseGeocodeCoords,
} from '../../../utils/geolocationUtils';

function SuggestionAvatar({ src }: { src?: string | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImg = Boolean(src) && src !== failedSrc;

  return (
    <span
      className={`home-native-search__suggestion-avatar${
        showImg ? '' : ' home-native-search__suggestion-avatar--placeholder'
      }`}
      aria-hidden
    >
      {showImg ? (
        <img src={src!} alt="" onError={() => setFailedSrc(src!)} />
      ) : (
        <User size={16} strokeWidth={2} />
      )}
    </span>
  );
}import './HomeNativeSearch.css';

interface PlacePrediction {
  description: string;
  place_id: string;
}

export interface HomeNativeSearchHandle {
  focusQuoi: () => void;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** Suggestions affichables (pros en tête, puis services, puis métiers pertinents). */
function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function textMatchesQuery(query: string, name: string): boolean {
  const q = normalizeSearchText(query);
  const target = normalizeSearchText(name);
  if (!q) return true;
  if (target.includes(q)) return true;
  const words = q.split(/\s+/).filter((word) => word.length >= 2);
  return words.length > 0 && words.every((word) => target.includes(word));
}

function proRelevanceScore(query: string, name: string): number {
  const q = normalizeSearchText(query);
  const target = normalizeSearchText(name);
  if (!q) return 0;
  if (target === q) return 100;
  if (target.startsWith(q)) return 90;
  if (target.includes(q)) return 80;
  const words = q.split(/\s+/).filter((word) => word.length >= 2);
  if (words.length > 0 && words.every((word) => target.includes(word))) return 70;
  return 0;
}

function buildSuggestionList(
  query: string,
  apiResults: AutocompleteItem[],
): AutocompleteItem[] {
  const q = query.trim();
  let professions = apiResults.filter((i) => i.type === 'profession');
  let services = apiResults.filter((i) => i.type === 'service');
  const pros = apiResults.filter((i) => i.type === 'pro');
  const associations = apiResults.filter((i) => i.type === 'association');

  if (q) {
    if (professions.length === 0 && pros.length === 0) {
      professions = findMatchingProfessionsStrict(q);
    }
    if (apiResults.length > 0) {
      professions = professions.filter((item) => textMatchesQuery(q, item.name));
    }
    services = services.filter((item) => textMatchesQuery(q, item.name));
  } else if (professions.length === 0) {
    professions = findMatchingProfessionsSimple('').slice(0, 6).map((p) => ({
      type: 'profession' as const,
      id: p.id,
      name: p.name,
      slug: p.slug ?? null,
      icon: null,
    }));
  }

  const sortedPros = [...pros].sort(
    (a, b) => proRelevanceScore(q, b.name) - proRelevanceScore(q, a.name),
  );

  const maxProfessions = q && sortedPros.length > 0 ? 3 : 6;

  return [
    ...sortedPros.slice(0, 4),
    ...services.slice(0, 3),
    ...professions.slice(0, maxProfessions),
    ...associations.slice(0, 2),
  ];
}

const HomeNativeSearch = forwardRef<HomeNativeSearchHandle>((_, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('native', { keyPrefix: 'search' });
  const { isFeatureEnabled } = useFeatureFlags();
  const discoveryOn = isFeatureEnabled(FeatureFlag.PRO_DISCOVERY);
  const associationsOn = isFeatureEnabled(FeatureFlag.ASSOCIATION_PAGE);
  const geoCountry = useGeoCountry();
  const askEnabled = isBookingAskAvailable({
    bookingAskEnabled: isFeatureEnabled(FeatureFlag.BOOKING_ASK),
    discoveryEnabled: discoveryOn,
    geoCountry,
  });
  useGoogleMapsConsumer();
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();
  const quoiRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const placesServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesSessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [searchQuoi, setSearchQuoi] = useState('');
  const [searchOu, setSearchOu] = useState('');
  const [selectedProfessionSlug, setSelectedProfessionSlug] = useState<string | null>(null);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<number | null>(null);
  const [selectedServiceTypeName, setSelectedServiceTypeName] = useState<string | null>(null);

  const [apiSuggestions, setApiSuggestions] = useState<AutocompleteItem[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [ouPredictions, setOuPredictions] = useState<PlacePrediction[]>([]);
  const [showOuSuggestions, setShowOuSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Ignore les réponses Places après un « Autour de moi » GPS (évite course + setState fantôme). */
  const gpsLabelLockRef = useRef(false);
  const nearMeGenRef = useRef(0);

  const debouncedQuoi = useDebouncedValue(searchQuoi, 280);
  const debouncedOu = useDebouncedValue(searchOu, 250);
  const suggestions = buildSuggestionList(searchQuoi, apiSuggestions);

  useImperativeHandle(ref, () => ({
    focusQuoi: () => {
      quoiRef.current?.focus();
      setShowSuggestions(true);
    },
  }));

  useEffect(() => {
    if (!debouncedQuoi.trim()) {
      setApiSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestionsLoading(true);
    fetchAutocomplete(debouncedQuoi)
      .then((res) => { if (!cancelled) setApiSuggestions(res); })
      .catch(() => { if (!cancelled) setApiSuggestions([]); })
      .finally(() => { if (!cancelled) setSuggestionsLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuoi]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setShowOuSuggestions(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  // Init du service Google Places une fois le SDK chargé.
  useEffect(() => {
    if (!isGoogleMapsLoaded || !window.google?.maps?.places) return;
    placesServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesSessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [isGoogleMapsLoaded]);

  // Prédictions ville / code postal pour le champ « Où ».
  useEffect(() => {
    const value = debouncedOu.trim();
    if (gpsLabelLockRef.current || !placesServiceRef.current || value.length < 2) {
      setOuPredictions([]);
      return;
    }
    let cancelled = false;
    try {
      placesServiceRef.current.getPlacePredictions(
        {
          input: value,
          sessionToken: placesSessionRef.current ?? undefined,
          componentRestrictions: {
            country: placesSearchCountryRestriction(geoCountry),
          },
          types: ['(regions)'],
        },
        (results, status) => {
          if (cancelled || gpsLabelLockRef.current) return;
          try {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              setOuPredictions(
                results.map((r) => ({ description: r.description, place_id: r.place_id })),
              );
            } else {
              setOuPredictions([]);
            }
          } catch {
            setOuPredictions([]);
          }
        },
      );
    } catch {
      setOuPredictions([]);
    }
    return () => { cancelled = true; };
  }, [debouncedOu, geoCountry]);

  const handleSelectOu = useCallback((prediction: PlacePrediction) => {
    gpsLabelLockRef.current = false;
    setSearchOu(prediction.description);
    setCoords(null);
    setOuPredictions([]);
    setShowOuSuggestions(false);
    if (window.google?.maps?.places) {
      placesSessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  const handleSelect = useCallback((item: AutocompleteItem) => {
    setErrorMessage(null);
    if (item.type === 'pro' && item.slug) {
      navigate(`/pro/${item.slug}`);
      setShowSuggestions(false);
      return;
    }
    if (item.type === 'association' && item.slug) {
      if (!associationsOn) return;
      navigate(`/association/${item.slug}`);
      setShowSuggestions(false);
      return;
    }
    if (item.type === 'profession') {
      setSelectedProfessionSlug(item.slug ?? null);
      setSelectedServiceTypeId(null);
      setSelectedServiceTypeName(null);
      setSearchQuoi(item.name);
    } else if (item.type === 'service') {
      setSearchQuoi(item.name);
      setSelectedProfessionSlug(null);
      setSelectedServiceTypeId(item.id);
      setSelectedServiceTypeName(item.name);
    }
    setShowSuggestions(false);
  }, [navigate, associationsOn]);

  const handleNearMe = async () => {
    if (isLocating || isSubmitting) return;
    if (!isMountedRef.current) return;

    const gen = ++nearMeGenRef.current;
    setErrorMessage(null);
    setShowOuSuggestions(false);
    setShowSuggestions(false);
    setIsLocating(true);

    try {
      const position = await getCurrentCoordinates();
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;
      const { latitude, longitude } = position;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setErrorMessage(t('locationUnavailable'));
        setCoords(null);
        return;
      }
      const label =
        (await reverseGeocodeCoords(latitude, longitude, i18n.language)) ?? t('nearMe');
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;

      gpsLabelLockRef.current = true;
      setCoords({ lat: latitude, lon: longitude });
      setSearchOu(label);
      setOuPredictions([]);
      setShowOuSuggestions(false);
    } catch (error) {
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;
      if (error instanceof GeolocationRequestError) {
        if (error.code === 'denied') {
          setErrorMessage(t('locationDenied'));
        } else if (error.code === 'timeout') {
          setErrorMessage(t('locationTimeout'));
        } else if (error.code === 'unavailable' || error.code === 'unsupported') {
          setErrorMessage(t('locationUnavailable'));
        } else {
          setErrorMessage(t('locationFailed'));
        }
      } else {
        setErrorMessage(t('locationFailed'));
      }
      setCoords(null);
    } finally {
      if (isMountedRef.current && gen === nearMeGenRef.current) {
        setIsLocating(false);
      }
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    setShowOuSuggestions(false);
    setIsSubmitting(true);
    try {
      if (!discoveryOn) {
        setErrorMessage(t('searchFailed'));
        return;
      }
      const result = await submitDiscoveryOrAsk({
        searchQuoi,
        searchOu: searchOu.trim(),
        selectedProfessionSlug,
        selectedServiceTypeId,
        selectedServiceTypeName,
        lat: coords?.lat ?? 0,
        lon: coords?.lon ?? 0,
        currentLocationSearch: location.search,
        country: geoCountry,
        askEnabled,
      });
      if (result.status === 'need_location') {
        setErrorMessage(t('needLocation'));
        return;
      }
      if (result.status === 'incomplete') {
        setErrorMessage(t('incomplete'));
        setShowSuggestions(true);
        quoiRef.current?.focus();
        return;
      }
      navigate(result.path);
    } catch {
      setErrorMessage(t('searchFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={wrapRef} className="home-native-search">
      <div className="home-native-search__card">
        {/* ── Je cherche ── */}
        <div className="home-native-search__field">
          <label className="home-native-search__label" htmlFor="home-native-quoi">
            {t('labelQuoi')}
          </label>
          <div className="home-native-search__input-wrap">
            <Search size={18} className="home-native-search__input-icon" aria-hidden />
            <input
              ref={quoiRef}
              id="home-native-quoi"
              className="home-native-search__input"
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              placeholder={askEnabled ? t('quoiPlaceholderAsk') : t('quoiPlaceholder')}
              value={searchQuoi}
              onChange={(e) => {
                setSearchQuoi(e.target.value);
                setSelectedProfessionSlug(null);
                setSelectedServiceTypeId(null);
                setSelectedServiceTypeName(null);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
            />
          </div>
        </div>

        {/* Suggestions inline — pousse le contenu, pas de overlay fixed */}
        {showSuggestions && (
          <div className="home-native-search__suggestions" role="listbox">
            {suggestionsLoading && debouncedQuoi.trim() ? (
              <div className="home-native-search__suggestions-status">
                <Loader2 size={16} className="home-native-search__spin" />
                <span>{t('searching')}</span>
              </div>
            ) : suggestions.length > 0 ? (
              suggestions.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="home-native-search__suggestion"
                  onClick={() => handleSelect(item)}
                >
                  {item.type === 'pro' ? <SuggestionAvatar src={item.icon} /> : null}
                  <span className="home-native-search__suggestion-body">
                    <span className="home-native-search__suggestion-name">{item.name}</span>
                    <span className="home-native-search__suggestion-type">
                      {t(`suggestionTypes.${item.type}`)}
                    </span>
                  </span>
                  <ChevronRight size={16} className="home-native-search__suggestion-chevron" />
                </button>
              ))
            ) : debouncedQuoi.trim() ? (
              <p className="home-native-search__suggestions-empty">
                {t('noResults')}
              </p>
            ) : null}
          </div>
        )}

        <div className="home-native-search__divider" />

        {/* ── Où ── */}
        <div className="home-native-search__field">
          <div className="home-native-search__field-header">
            <label className="home-native-search__label" htmlFor="home-native-ou">
              {t('labelOu')}
            </label>
            <button
              type="button"
              className={`home-native-search__near-me${coords ? ' is-active' : ''}`}
              onClick={() => void handleNearMe()}
              disabled={isLocating || isSubmitting}
              aria-label={t('nearMe')}
            >
              {isLocating ? (
                <Loader2 size={12} className="home-native-search__spin" aria-hidden />
              ) : (
                <LocateFixed size={12} aria-hidden />
              )}
              <span className="home-native-search__near-me-label">
                {isLocating ? t('locating') : t('nearMe')}
              </span>
            </button>
          </div>
          <div className="home-native-search__input-wrap">
            <MapPin size={18} className="home-native-search__input-icon" aria-hidden />
            <input
              id="home-native-ou"
              className="home-native-search__input"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              placeholder={t('ouPlaceholder')}
              value={searchOu}
              onChange={(e) => {
                gpsLabelLockRef.current = false;
                setSearchOu(e.target.value);
                setCoords(null);
                setShowSuggestions(false);
                setShowOuSuggestions(true);
              }}
              onFocus={() => {
                setShowSuggestions(false);
                setShowOuSuggestions(true);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSubmit(); }}
            />
          </div>
        </div>

        {/* Suggestions ville (Google Places) — inline */}
        {showOuSuggestions && ouPredictions.length > 0 && (
          <div className="home-native-search__suggestions" role="listbox">
            {ouPredictions.map((pred) => (
              <button
                key={pred.place_id}
                type="button"
                role="option"
                aria-selected={false}
                className="home-native-search__suggestion"
                onClick={() => handleSelectOu(pred)}
              >
                <MapPin size={16} className="home-native-search__input-icon" aria-hidden />
                <span className="home-native-search__suggestion-body">
                  <span className="home-native-search__suggestion-name">{pred.description}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          className="home-native-search__submit"
          disabled={isSubmitting}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="home-native-search__spin" />
              {t('searching')}
            </>
          ) : (
            t('submit')
          )}
        </button>
      </div>

      {errorMessage && (
        <p className="home-native-search__error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
});

HomeNativeSearch.displayName = 'HomeNativeSearch';

export default HomeNativeSearch;
